import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2, Mail, Lock } from 'lucide-react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import * as Sentry from '@sentry/react';

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const successMessage = (location.state as any)?.message ?? '';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Watch for infinite loading spinners
    useEffect(() => {
        if (!isLoading) return;
        const timer = setTimeout(() => {
            Sentry.captureMessage("UI bloqueada en estado de carga: Componente Login", "warning");
        }, 10000);
        return () => clearTimeout(timer);
    }, [isLoading]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage('');
        setIsLoading(true);

        try {
            const response = await authService.login(email, password);
            login(response.token);
            navigate('/wallet');
        } catch (err: any) {
            setErrorMessage(err.message || 'Error al iniciar sesión.');
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        if (!credentialResponse.credential) return;
        setErrorMessage('');
        setIsGoogleLoading(true);
        try {
            const response = await authService.loginWithGoogle(credentialResponse.credential);
            login(response.token);
            navigate('/wallet');
        } catch (err: any) {
            setErrorMessage(err.message || 'Error al iniciar sesión con Google.');
            setIsGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        setErrorMessage('No se pudo iniciar sesión con Google. Por favor, inténtalo de nuevo.');
    };


    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white shadow-xl">
            {/* Logo top-left */}
            <div className="px-5 pt-5">
                <img src="/logo.svg" alt="Logo" className="h-10 w-10 object-contain" />
            </div>
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                {/* Header */}
                <div className="mb-10 text-center">
                    <h1 className="text-3xl font-bold text-gray-900">Bienvenido a</h1>
                    <h1 className="text-3xl font-bold text-gray-900">Zovy Rewards</h1>
                    <p className="mt-2 text-sm text-gray-500">
                        Inicia sesión para acceder a tus puntos y recompensas
                    </p>
                </div>

                {/* Success Message (from password reset) */}
                <div className={`mb-6 rounded-2xl bg-green-50 px-4 py-3 text-center text-sm font-medium text-green-600 transition-all ${successMessage ? 'block' : 'hidden'}`}>
                    {successMessage || '\u00A0'}
                </div>

                {/* Error Message */}
                <div className={`mb-6 rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600 transition-all ${errorMessage ? 'block' : 'hidden'}`}>
                    {errorMessage || '\u00A0'}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} translate="no" className="flex flex-col gap-4">
                    {/* Email */}
                    <div>
                        <label
                            htmlFor="email"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Correo electrónico
                        </label>
                        <div className="relative">
                            <Mail
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="email"
                                type="email"
                                required
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@correo.com"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label
                            htmlFor="password"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Contraseña
                        </label>
                        <div className="relative">
                            <Lock
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="password"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Iniciando sesión...
                            </>
                        ) : (
                            <span>Iniciar sesión</span>
                        )}
                    </button>
                </form>

                {/* Divider */}
                <div className="relative my-6 flex items-center">
                    <div className="flex-grow border-t border-gray-200" />
                    <span className="mx-4 flex-shrink text-xs font-medium text-gray-400">
                        o continúa con
                    </span>
                    <div className="flex-grow border-t border-gray-200" />
                </div>

                {/* Google Login */}
                <div className={`relative flex justify-center ${isGoogleLoading ? 'pointer-events-none opacity-60' : ''}`}>
                    {isGoogleLoading ? (
                        <div className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 py-3.5 text-sm font-medium text-gray-600">
                            <Loader2 size={18} className="animate-spin text-gray-400" />
                            Iniciando sesión con Google...
                        </div>
                    ) : (
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            theme="outline"
                            shape="rectangular"
                            text="signin_with"
                        />
                    )}
                </div>

                <Link
                    to="/forgot-password"
                    className="mt-5 block text-center text-sm font-medium text-indigo-600 hover:text-indigo-700"
                >
                    ¿Has olvidado tu contraseña?
                </Link>

                <p className="mt-4 text-center text-sm text-gray-500">
                    ¿No tienes cuenta?{' '}
                    <Link
                        to="/register"
                        className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                        Regístrate
                    </Link>
                </p>

                <p className="mt-6 text-center text-xs text-gray-500">
                    Al iniciar sesión, aceptas nuestros{' '}
                    <Link to="/terms" className="font-medium hover:text-indigo-600 transition-colors">Términos y Condiciones</Link>
                    {' '}y nuestra{' '}
                    <Link to="/privacy" className="font-medium hover:text-indigo-600 transition-colors">Política de Privacidad</Link>.
                </p>
            </div>
        </div>
    );
}
